import React, { useState, useMemo, useCallback, useEffect } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { Project } from '@models/project.model';
import { EmptyData } from '@components/molecules/EmptyData';
import { usePendingIncidents } from '@hooks/api/incident/usePendingIncidents';
import { usePendingBudgetRequests } from '@hooks/api/budget/usePendingBudgetRequests';
import { usePendingApprovals } from '@hooks/api/task/usePendingApprovals';
import { NeedsYourActionBlock } from '@components/organisms/NeedsYourActionBlock';
import { useIncident } from '@hooks/api';
import { Incident } from '@models/incident.model';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import AcknowledgeIncidentModal from '@components/organisms/AcknowledgeIncidentModal';
import ProjectGeofenceModal from '@components/organisms/ProjectGeofenceModal';
import { useProjectFeedByProjects } from '@pages/projects/components/ProjectFeed/hooks';
import { useProjects, useToggleProjectFavorite } from '@hooks/api/projects';
import { usePendingInvoicesCount } from '@hooks/api';
import {
  TriageFilter,
  triageFilterToTypes,
} from '@components/molecules/TriageFilterBar/TriageFilterBar.types';
import { ActivityResolutionStatus } from '@models/activity.model';
import { useTask } from '@hooks/api';
import { TaskDetailModal } from '@components/organisms/TaskDetailModal';
import TrialExpiredBanner from '@components/organisms/TrialExpiredBanner';
import { useFeatureAccess } from '@utils/featureFlag';
import { useHistory } from 'react-router-dom';
import { ProjectStatus } from '@models/projectStatus.model';
import {
  DashboardHeader,
  ProjectFilter,
  ActiveProjectsList,
  DashboardContent,
} from './components';
import { useTranslation } from '@hooks/useTranslation';

const DashboardPage: React.FC = () => {
  // Sprint 28, story-144. `dashboard.*` — docs/architecture.md "## Sprint 28" Decision 1.
  const tDashboard = useTranslation('dashboard');
  const history = useHistory();
  const { canAccess } = useFeatureAccess();
  const canAccessProjects = canAccess('DASHBOARD');
  const canInvoice = canAccess('INVOICES');
  const { organizationId } = useAppSelector(
    (state: RootState) => state.organization,
  );
  const { isReadOnly } = useAppSelector((state: RootState) => state.user);

  // State management
  const [isIncidentModalOpen, setIsIncidentModalOpen] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(
    null,
  );
  const [isTaskDetailModal, setIsTaskDetailModal] = useState(false);
  const [isGeofenceModalOpen, setIsGeofenceModalOpen] = useState(false);
  const [geofenceProject, setGeofenceProject] = useState<Project | null>(null);
  const [selectedProject, setSelectedProject] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [triageFilter, setTriageFilter] = useState<TriageFilter>('all');

  // React Query hooks
  const {
    data: pendingIncidents = [],
    isLoading: incidentLoading,
    refetch: refetchPendingIncidents,
  } = usePendingIncidents();
  const {
    data: budgetRequestsData = [],
    isLoading: budgetLoading,
    refetch: refetchBudgetRequests,
  } = usePendingBudgetRequests(organizationId || undefined);

  // Sprint 16, story-075/076. Net-new mount at the dashboard level — moved up from where
  // `ApprovalsNotification` used to own it in the sidebar (now deleted). Same call shape
  // `usePendingBudgetRequests` already uses (`organizationId || undefined`).
  const {
    data: pendingApprovals = [],
    isLoading: approvalsLoading,
    refetch: refetchPendingApprovals,
  } = usePendingApprovals(organizationId || undefined);

  const { data: projects, isLoading: projectsLoading, refetch: refetchProjects } = useProjects();

  // The pending budget requests hook already returns filtered data
  const pendingBudgets = budgetRequestsData;

  const { acknowledgeIncident } = useIncident();
  const { fetchTask, task } = useTask();

  const projectsIds = useMemo(() => {
    if (selectedProject) {
      return [selectedProject.value];
    }
    if (!projects || projects.length === 0) {
      return [];
    }
    return projects
      .map((project) => project.projectId)
      .filter((id): id is string => Boolean(id));
  }, [projects, selectedProject]);

  // Use ProjectFeed hook for project activities, server-side filtered by the
  // active triage pill (re-queries + resets pagination on change).
  const feedTypes = useMemo(() => triageFilterToTypes(triageFilter), [triageFilter]);

  // Every triage pill is an action queue: show only unresolved (ACTIVE) alerts
  // (open incidents, open bids, unpaid invoices). "All Activity" keeps showing
  // resolved items as historical record.
  const feedResolutionStatus =
    triageFilter === 'all' ? undefined : ActivityResolutionStatus.ACTIVE;

  const { activities, paginationActions, totalCount, refetchActivities } =
    useProjectFeedByProjects({
      projectsId: projectsIds,
      types: feedTypes,
      resolutionStatus: feedResolutionStatus,
    });

  // Skip polling invoices entirely when the feature is off (mirrors the sidebar
  // and mobile gating); they must not count toward the triage pills either.
  const { data: pendingInvoicesCount = 0 } = usePendingInvoicesCount(
    canInvoice ? projectsIds : [],
  );

  const projectOptions = useMemo(() => {
    if (!projects) return [];
    return projects.map((project) => ({
      value: project.projectId!,
      label: project.name,
    }));
  }, [projects]);

  // Unified refresh handler for all dashboard data
  const handleRefreshAll = useCallback(() => {
    refetchPendingIncidents();
    refetchBudgetRequests();
    refetchPendingApprovals();
    refetchProjects();
    refetchActivities();
  }, [
    refetchPendingIncidents,
    refetchBudgetRequests,
    refetchPendingApprovals,
    refetchProjects,
    refetchActivities,
  ]);

  const isRefreshing =
    incidentLoading ||
    budgetLoading ||
    approvalsLoading ||
    projectsLoading ||
    paginationActions?.isLoading;

  const handleAcknowledgeConfirm = async () => {
    if (selectedIncident) {
      const result = await acknowledgeIncident(selectedIncident.incidentId);
      if (result) {
        // Refetch pending incidents to get updated data
        refetchPendingIncidents();
        setIsIncidentModalOpen(false);
        setSelectedIncident(null);
      }
    }
  };

  const toggleAcknowledgeIncidentModal = () => {
    setIsIncidentModalOpen(!isIncidentModalOpen);
    setSelectedIncident(null);
  };

  const handleOpenTaskDetail = (taskId: string) => {
    fetchTask(taskId);
    setIsTaskDetailModal(true);
  };

  const handleProjectClick = useCallback(
    (projectId: string) => {
      history.push(`/projects/${projectId}`);
    },
    [history],
  );

  const handleProjectFilterChange = useCallback(
    (option: { value: string; label: string } | null) => {
      setSelectedProject(option);
    },
    [],
  );

  const handleShowGeofence = useCallback((project: Project) => {
    setGeofenceProject(project);
    setIsGeofenceModalOpen(true);
  }, []);

  const { mutate: toggleFavorite } = useToggleProjectFavorite();

  const handleToggleFavorite = useCallback(
    (projectId: string) => {
      toggleFavorite(projectId);
    },
    [toggleFavorite],
  );

  // Active projects, with favorited ones surfaced first. Array.sort is stable,
  // so non-favorited projects keep their existing relative order.
  const activeProjects = useMemo(
    () =>
      (projects || [])
        .filter((p) => p.state?.toLowerCase() === ProjectStatus.ONGOING)
        .sort((a, b) => Number(b.isFavorited) - Number(a.isFavorited)),
    [projects],
  );

  const triageCounts = useMemo(
    () => ({
      incident: pendingIncidents.length,
      budget: pendingBudgets.length,
      invoice: canInvoice ? pendingInvoicesCount : 0,
    }),
    [pendingIncidents.length, pendingBudgets.length, pendingInvoicesCount, canInvoice],
  );

  // Empty-state routing. "Empty" = no activities AND no notifications (every
  // triage count zero). With zero projects → offer Create Project; with projects
  // → deep-link straight into the first one, but only on the post-login landing.
  const firstProjectId = useMemo(
    () =>
      (projects || [])
        .map((p) => p.projectId)
        .filter((id): id is string => Boolean(id))[0],
    [projects],
  );
  const hasProjects = Boolean(firstProjectId);
  const noActivities = totalCount === 0;
  const noNotifications =
    triageCounts.incident + triageCounts.budget + triageCounts.invoice === 0;
  const dataReady =
    !projectsLoading &&
    !paginationActions?.isLoading &&
    !incidentLoading &&
    !budgetLoading;

  // Fire once per session (i.e. the post-login landing). Later manual visits to
  // the dashboard leave the user on it instead of bouncing them into a project.
  useEffect(() => {
    if (!canAccessProjects || !dataReady) return;
    if (sessionStorage.getItem('dashboardLandingHandled')) return;
    sessionStorage.setItem('dashboardLandingHandled', '1');
    if (noActivities && hasProjects && firstProjectId) {
      history.replace(`/projects/${firstProjectId}`);
    }
  }, [
    canAccessProjects,
    dataReady,
    noActivities,
    hasProjects,
    firstProjectId,
    history,
  ]);

  if (!canAccessProjects) {
    return <div>{tDashboard.noAccess}</div>;
  }

  // Nothing at all yet → prompt to create the first project.
  if (dataReady && noActivities && noNotifications && !hasProjects) {
    return (
      <>
        <div className="flex flex-1 w-full h-full">
          <EmptyData
            fullHeight
            className="!border-0"
            textButton={tDashboard.createProject}
            onClickButton={() => history.push('/projects/new')}
            description={tDashboard.noProjectsYet}
            iconButton={<AddIcon sx={{ fontSize: '16px' }} />}
            disabled={isReadOnly}
          />
        </div>
      </>
    );
  }

  return (
    <>
      {/* Renders nothing unless the trial is ending soon or has already ended. */}
      <TrialExpiredBanner />

      <div className="grid grid-cols-1 md:grid-cols-[60%_40%] w-full gap-3">
        {/* Feed column: capped to the viewport minus the content wrapper's vertical
            padding (py-6 = 3rem), so the grid is exactly the viewport height and
            `main` never scrolls. The header + filters stay fixed; only the feed
            scrolls internally (see DashboardContent). */}
        <div className="flex flex-col gap-3 min-h-0 h-[calc(100vh-3rem)]">
          <DashboardHeader />

          <DashboardContent
            activities={activities}
            paginationActions={paginationActions}
            totalCount={totalCount}
            isActivitiesLoading={paginationActions?.isLoading}
            onOpenTaskDetail={handleOpenTaskDetail}
            onProjectClick={handleProjectClick}
            triageFilter={triageFilter}
            onTriageChange={setTriageFilter}
            triageCounts={triageCounts}
            canInvoice={canInvoice}
            needsYourActionSlot={
              <NeedsYourActionBlock
                approvals={pendingApprovals}
                approvalsLoading={approvalsLoading}
                bids={pendingBudgets}
                bidsLoading={budgetLoading}
                incidents={pendingIncidents}
                incidentsLoading={incidentLoading}
                onRefetchApprovals={refetchPendingApprovals}
              />
            }
            filterSlot={
              <ProjectFilter
                selectedProject={selectedProject}
                projectOptions={projectOptions}
                onProjectChange={handleProjectFilterChange}
                onRefresh={handleRefreshAll}
                isRefreshing={isRefreshing}
              />
            }
          />
        </div>

        <ActiveProjectsList
          projects={activeProjects}
          isLoading={projectsLoading}
          onProjectClick={handleProjectClick}
          onShowGeofence={handleShowGeofence}
          onToggleFavorite={handleToggleFavorite}
        />
      </div>

      <TaskDetailModal
        task={task}
        isTaskDetailModal={isTaskDetailModal}
        setIsTaskDetailModal={setIsTaskDetailModal}
        project={
          projects?.find((p) => task?.projectId === p.projectId) as Project
        }
      />

      <AcknowledgeIncidentModal
        open={isIncidentModalOpen}
        incident={selectedIncident}
        isAcknowledging={incidentLoading}
        onConfirm={handleAcknowledgeConfirm}
        onClose={toggleAcknowledgeIncidentModal}
      />

      <ProjectGeofenceModal
        isOpen={isGeofenceModalOpen}
        onClose={() => {
          setIsGeofenceModalOpen(false);
          setGeofenceProject(null);
        }}
        project={geofenceProject}
        hideActiveToggle
      />
    </>
  );
};

export default DashboardPage;
